/**
 * Zone Datastores Tab Component
 *
 * Displays a table of datastores for the current zone.
 */

import { useParams } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getZoneDatastores } from '../../../services/capacity'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'

/** Get usage color class */
const getUsageClass = (pct: number): string => {
    if (pct >= 85) return 'text-red-600 font-semibold'
    if (pct >= 70) return 'text-amber-600'
    return 'text-green-600'
}

/** Get progress bar color */
const getProgressColor = (pct: number): string => {
    if (pct >= 85) return 'bg-red-500'
    if (pct >= 70) return 'bg-amber-500'
    return 'bg-green-500'
}

const ZoneDatastores = () => {
    const { zoneId } = useParams<{ zoneId: string }>()

    const { data: datastores, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ZONE_DATASTORES, zoneId],
        queryFn: () => getZoneDatastores(zoneId!),
        enabled: !!zoneId,
    })

    if (isLoading) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Datastores</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (!datastores || datastores.length === 0) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Datastores</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        No datastores found in this zone
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle>Datastores ({datastores.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Datastore Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead className="text-right">
                                    Capacity
                                </TableHead>
                                <TableHead className="text-right">
                                    Used
                                </TableHead>
                                <TableHead className="text-right">
                                    Free
                                </TableHead>
                                <TableHead className="w-[200px]">
                                    Usage
                                </TableHead>
                                <TableHead className="text-center">
                                    Accessible
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {datastores.map((ds) => {
                                // Calculate used_gb if not provided by API
                                const capacityGb = ds.capacity_gb ?? 0
                                const freeGb = ds.free_gb ?? 0
                                const usedGb = ds.used_gb ?? capacityGb - freeGb
                                const usagePct =
                                    capacityGb > 0
                                        ? (usedGb / capacityGb) * 100
                                        : 0

                                return (
                                    <TableRow key={ds.id}>
                                        <TableCell className="font-medium">
                                            {ds.name}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {ds.type?.toUpperCase() ||
                                                    'N/A'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {capacityGb > 0
                                                ? `${capacityGb.toLocaleString()} GB`
                                                : '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {capacityGb > 0
                                                ? `${usedGb.toLocaleString()} GB`
                                                : '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {capacityGb > 0
                                                ? `${freeGb.toLocaleString()} GB`
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Progress
                                                    value={usagePct}
                                                    className="h-2 flex-1"
                                                    indicatorClassName={getProgressColor(
                                                        usagePct
                                                    )}
                                                />
                                                <span
                                                    className={`text-sm w-12 text-right ${getUsageClass(usagePct)}`}
                                                >
                                                    {usagePct.toFixed(0)}%
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge
                                                variant={
                                                    ds.accessible
                                                        ? 'default'
                                                        : 'destructive'
                                                }
                                                className={
                                                    ds.accessible
                                                        ? 'bg-green-100 text-green-700 border-green-200'
                                                        : 'bg-red-100 text-red-700 border-red-200'
                                                }
                                            >
                                                {ds.accessible ? 'Yes' : 'No'}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    )
}

export default ZoneDatastores
